import React, { useCallback } from 'react';

import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';

import { SellerSettings } from '../../../enums/seller-settings/seller-settings';
import { SellerSettingsOptions } from '../../../enums/seller-settings/seller-settings-options';
import { getSellerSettingOptions, getSelectedOptionMessageDescriptor } from '../../../services/settings/seller-settings-service';
import messages from './messages';
import style from './style.module.less';

const emptyMessageReplacement = <div style={{ height: '14px' }} />;
const iTNSettingOptions = getSellerSettingOptions(SellerSettings.iTNSetting);
const defaultExemptionCodeOptions = getSellerSettingOptions(SellerSettings.defaultExemptionCode);

const getNoteMessage = (value) => {
  const map = {
    [SellerSettingsOptions.itn.value]: (
      <FormattedMessage
        {...messages.itnTaxNote}
        values={{
          b: (chunks) => <b>{chunks}</b>,
        }}
      />
    ),
    [SellerSettingsOptions.uspsExemptionCode.value]: (
      <FormattedMessage
        {...messages.defaultUSPSExepmtionCodeNote}
        values={{
          b: (chunks) => <b>{chunks}</b>,
          a: (chunks) => (
            <a
              href='https://www.ecfr.gov/cgi-bin/text-idx?SID=a4601ebeb6d2db46932a69a2cb8a216f&mc=true&node=sp15.1.30.d&rgn=div6'
              rel='noreferrer'
              target='_blank'
            >
              {chunks}
            </a>
          ),
        }}
      />
    ),
  };
  return map[value] || null;
};

const ItnVatUsps = ({ exemptionOrITN = '', iTNSetting = '', defaultExemptionCode = '', setITNSetting, setDefaultExemptionCode }) => {
  const getSelectedITNSettingMessageDescriptor = useCallback(
    () => getSelectedOptionMessageDescriptor(iTNSetting, iTNSettingOptions),
    [iTNSetting],
  );

  if (exemptionOrITN === SellerSettingsOptions.empty.value) {
    return null;
  }

  return (
    <div className={style.subsection}>
      {SellerSettingsOptions.itn.value === exemptionOrITN && (
        <div className={style.setting}>
          <div className={style['setting-label']}>
            <FormattedMessage {...SellerSettings.iTNSetting.messageDescriptor} />
          </div>
          <div className={style['setting-options']}>
            <DropdownMenu className={style.dropdown} dropdownMenuShouldHaveWidthOfButton>
              <DropdownMenuToggler className={style['dropdown-selector']} theme='white'>
                {iTNSetting === '' ? emptyMessageReplacement : <FormattedMessage {...getSelectedITNSettingMessageDescriptor()} />}
              </DropdownMenuToggler>
              {iTNSettingOptions.map((option) => (
                <DropdownMenuItem key={option.value} onClick={() => setITNSetting(option.value)} selected={iTNSetting === option.value}>
                  {!option.messageDescriptor ? emptyMessageReplacement : <FormattedMessage {...option.messageDescriptor} />}
                </DropdownMenuItem>
              ))}
            </DropdownMenu>
          </div>
        </div>
      )}
      {exemptionOrITN === SellerSettingsOptions.uspsExemptionCode.value && (
        <div className={style.setting}>
          <div className={style['setting-label']}>
            <FormattedMessage {...SellerSettings.defaultExemptionCode.messageDescriptor} />
          </div>
          <div className={style['setting-options']}>
            <DropdownMenu className={style.dropdown} dropdownMenuShouldHaveWidthOfButton>
              <DropdownMenuToggler className={style['dropdown-selector']} theme='white'>
                {defaultExemptionCode ? <div>{defaultExemptionCode}</div> : emptyMessageReplacement}
              </DropdownMenuToggler>
              {defaultExemptionCodeOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => setDefaultExemptionCode(option.value)}
                  selected={defaultExemptionCode === option.value}
                >
                  {option.value}
                </DropdownMenuItem>
              ))}
            </DropdownMenu>
          </div>
        </div>
      )}
      {getNoteMessage(exemptionOrITN)}
    </div>
  );
};

ItnVatUsps.propTypes = {
  exemptionOrITN: PropTypes.string,
  iTNSetting: PropTypes.string,
  defaultExemptionCode: PropTypes.string,
  setITNSetting: PropTypes.func.isRequired,
  setDefaultExemptionCode: PropTypes.func.isRequired,
};

export default ItnVatUsps;
